<template>
    <section class="rulebook-validation">
        <b-alert v-if="error" show variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <b-button size="sm" variant="outline-danger" @click="$emit('retry')">다시 불러오기</b-button>
        </b-alert>

        <div v-if="loading" class="text-center text-muted py-5">
            <b-spinner small class="mr-2" />검증 상태를 불러오는 중입니다.
        </div>

        <template v-else>
            <b-alert v-if="reviewOnly" show variant="warning" class="status-alert mb-4">
                <div class="font-weight-bold mb-1">검증 게이트 없이 사후 검토로 운영 중</div>
                <div>
                    지금은 리뷰를 통과한 판단 기준 후보가 자동으로 활성화됩니다.
                    아래 지표는 관찰용이며, 이상이 있으면 기준 버전 탭에서 이전 버전으로 되돌립니다.
                </div>
            </b-alert>

            <b-alert :variant="status.variant" show class="status-alert mb-4">
                <div class="font-weight-bold mb-1">{{ status.label }}</div>
                <div>{{ status.description }}</div>
            </b-alert>

            <b-row class="mb-4">
                <b-col v-for="bucket in buckets" :key="bucket.label" cols="12" lg="4" class="mb-3">
                    <b-card class="bucket-card h-100" body-class="p-4">
                        <div class="d-flex align-items-start justify-content-between mb-2">
                            <div>
                                <div class="bucket-label">{{ bucket.label }}</div>
                                <div class="bucket-count">{{ bucket.value.toLocaleString() }}</div>
                            </div>
                            <b-badge :variant="bucket.variant">{{ bucket.badge }}</b-badge>
                        </div>
                        <p class="text-muted small mb-0">{{ bucket.description }}</p>
                    </b-card>
                </b-col>
            </b-row>

            <b-card class="mb-4" body-class="p-4">
                <h5 class="mb-2">자동 검증은 무엇을 하나요?</h5>
                <p class="text-muted mb-4">
                    새 후보와 지금 쓰는 판단 기준을 같은 회사 답변에 각각 적용합니다.
                    숨겨둔 정답과 비교해 {{ gateDescription }}
                </p>
                <b-row>
                    <b-col cols="12" md="4" class="mb-3 mb-md-0">
                        <div class="check-item">
                            <strong>근거 범주</strong>
                            <span>사람이 고친 범주와 맞는지</span>
                        </div>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-3 mb-md-0">
                        <div class="check-item">
                            <strong>충족 여부</strong>
                            <span>Y/N 판단이 맞는지</span>
                        </div>
                    </b-col>
                    <b-col cols="12" md="4">
                        <div class="check-item">
                            <strong>실패·누락</strong>
                            <span>어려운 사례를 피하지 않았는지</span>
                        </div>
                    </b-col>
                </b-row>
            </b-card>

            <b-card body-class="p-0">
                <b-button v-b-toggle.rulebook-assignment-details
                          block
                          variant="link"
                          class="detail-toggle text-left p-3"
                >
                    배정 방식 자세히 보기
                </b-button>
                <b-collapse id="rulebook-assignment-details">
                    <div class="detail-body p-4 border-top">
                        <h6>왜 사례를 두 곳으로 나누나요?</h6>
                        <p>
                            판단 기준을 만드는 데 본 사례로 다시 시험하면 답을 미리 본 것과 같습니다.
                            그래서 연결된 답변 묶음은 처음부터 한쪽에만 두고 이후에도 옮기지 않습니다.
                        </p>
                        <h6>‘고정 배정’은 무슨 뜻인가요?</h6>
                        <p>
                            묶음의 변하지 않는 고유정보로 추첨번호를 만들고, 약 20% 범위는 시험 전용으로 둡니다.
                            같은 묶음은 다시 실행해도 같은 번호를 받으므로 결과가 바뀌지 않습니다.
                            평가 점수나 회사의 우열을 계산하는 공식은 아닙니다.
                        </p>
                        <h6>보류 사례는 왜 생기나요?</h6>
                        <p class="mb-0">
                            답변을 다시 재생할 수 없거나, 이미 양쪽에 노출됐거나, 서로 연결된 묶음이 지나치게 큰 경우에는
                            잘못된 검증을 막기 위해 사용하지 않습니다.
                        </p>
                    </div>
                </b-collapse>
            </b-card>
        </template>
    </section>
</template>

<script>
import {validationStatusMeta} from './rulebook-presentation';

export default {
    name: 'RulebookValidationTab',
    props: {
        overview: {type: Object, required: true},
        loading: {type: Boolean, default: false},
        error: {type: String, default: ''},
    },
    computed: {
        status() {
            return validationStatusMeta(this.overview.validation);
        },
        reviewOnly() {
            return this.overview.automation.activationMode === 'review_only';
        },
        gateDescription() {
            // review_only 에서는 이 비교가 활성화 게이트가 아니라 관찰 지표다.
            return this.reviewOnly
                ? '얼마나 달라지는지 확인합니다. 지금은 관찰용이며 활성화를 막지 않습니다.'
                : '하나라도 나빠지면 후보를 실제 평가에 적용하지 않습니다.';
        },
        buckets() {
            return [
                {
                    label: '기준 만드는 사례',
                    value: this.overview.validation.corpus,
                    badge: '원칙 추출용',
                    variant: 'primary',
                    description: '피드백의 공통 문맥을 찾아 새 평가원칙을 만드는 데 사용합니다.',
                },
                {
                    label: '시험 전용 사례',
                    value: this.overview.validation.holdout,
                    badge: '정답 비교용',
                    variant: 'success',
                    description: '판단 기준을 만들 때는 보여주지 않고 새 후보의 품질을 확인할 때만 사용합니다.',
                },
                {
                    label: '보류 사례',
                    value: this.overview.validation.quarantined,
                    badge: '사용 안 함',
                    variant: 'secondary',
                    description: '재생이나 분리가 확실하지 않아 현재 판단 기준과 검증에 모두 사용하지 않습니다.',
                },
            ];
        },
    },
};
</script>

<style scoped lang="scss">
.status-alert {
    border-width: 1px;
}

.bucket-card {
    border-color: #e0e5eb;
}

.bucket-label {
    color: #5c6775;
    font-size: 0.82rem;
    font-weight: 700;
}

.bucket-count {
    color: #233142;
    font-size: 1.9rem;
    font-weight: 700;
    margin-top: 0.25rem;
}

.check-item {
    background: #f8f9fa;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 1rem;

    strong {
        color: #29384a;
        margin-bottom: 0.35rem;
    }

    span {
        color: #687485;
        font-size: 0.85rem;
    }
}

.detail-toggle {
    color: #34495e;
    font-weight: 600;
    text-decoration: none;

    &:hover,
    &:focus {
        text-decoration: none;
    }
}

.detail-body {
    background: #fafbfc;
    color: #465467;
    font-size: 0.9rem;
    line-height: 1.65;

    h6 {
        color: #29384a;
        font-weight: 700;
    }
}
</style>
